import type { ComposerPolicy } from "../engine"

/**
 * The learning frameworks HR can select, and what each one actually DOES.
 *
 * The point of this file is the `patch`. A framework picker that only changed a
 * label would be decoration; every entry here rewrites composer parameters, so
 * selecting one visibly changes the plans, the coverage, or the cost. Where a
 * framework's mechanic could not be implemented honestly it is not listed.
 *
 * Two families, and they combine differently:
 *
 *  - **Ladders** (Kirkpatrick, Phillips, LTEM) are competing answers to the same
 *    question — what counts as proof — so exactly one may be active.
 *  - **Overlays and design frameworks** (Brinkerhoff, 70-20-10, Spacing) answer
 *    different questions and stack freely.
 *
 * That asymmetry is not a UI convenience; it is what the literature actually
 * says. Phillips is Kirkpatrick plus a fifth level, not a rival to be run
 * alongside it, whereas Brinkerhoff is a sampling method that presupposes some
 * ladder underneath.
 */

export type FrameworkId =
    | "kirkpatrick"
    | "phillips"
    | "ltem"
    | "brinkerhoff"
    | "seventy-twenty-ten"
    | "spacing"

export type FrameworkFamily = "ladder" | "overlay" | "design"

/** Radar axes — how a selection emphasises each concern, 0–4. */
export type FrameworkAxis = "nachweis" | "kosten" | "transfer" | "modalitaet" | "wiederholung"

export const FRAMEWORK_AXES: FrameworkAxis[] = [
    "nachweis",
    "kosten",
    "transfer",
    "modalitaet",
    "wiederholung",
]

export interface LearningFramework {
    id: FrameworkId
    name: string
    author: string
    year: number
    family: FrameworkFamily
    /** One line: what this framework argues. */
    claim: string
    /** "Was sich ändert" — the concrete parameter effects, in plain language. */
    changes: string[]
    /** The headline number it moves, so the demo knows where to point. */
    watch: string
    patch: ComposerPolicy
    profile: Record<FrameworkAxis, number>
    source: string
}

export const FRAMEWORKS: LearningFramework[] = [
    {
        id: "kirkpatrick",
        name: "Kirkpatrick (New World)",
        author: "Kirkpatrick & Kirkpatrick",
        year: 2016,
        family: "ladder",
        claim: "Vier Stufen — Reaktion, Lernen, Verhalten, Resultate — rückwärts geplant: erst das Ergebnis festlegen, dann das Programm.",
        changes: [
            "Ein Abschluss allein weist eine Pflicht nicht mehr nach; verlangt wird mindestens ein bestandener Wissenstest.",
            "Stufe 1 misst die Anwendungsabsicht, nicht die Zufriedenheit — Zielwert 4,0 von 5,0.",
            "Stufe 2 gilt ab 66 % als bestanden.",
            "Stufe 3 wird 14–30 Tage nach Abschluss durch die Führungskraft bestätigt, mit den vier Required Drivers.",
            "Stufe 4 wird 60–90 Tage danach gelesen.",
        ],
        watch: "Pflicht-Abdeckung",
        patch: {
            evidence: {
                floor: "wissenstest",
                gateObligationDischarge: true,
                reactionTarget: 4,
                learningPassScaled: 0.66,
                behaviourWindowDays: { from: 14, to: 30 },
                resultsWindowDays: { from: 60, to: 90 },
            },
        },
        profile: { nachweis: 3, kosten: 1, transfer: 3, modalitaet: 1, wiederholung: 1 },
        source: "https://www.kirkpatrickpartners.com/the-kirkpatrick-model/",
    },
    {
        id: "phillips",
        name: "Phillips ROI (Stufe 5)",
        author: "Jack J. Phillips",
        year: 1997,
        family: "ladder",
        claim: "Kirkpatrick plus eine fünfte Stufe: Nutzen und Kosten in Dollar, mit Isolation des Programmeffekts.",
        changes: [
            "Die Rangfolge rechnet in Dollar statt in Stunden — Nutzen pro Dollar statt Nutzen pro Stunde.",
            "Der Kostensatz je Format wird aktiv (Präsenz 150 USD/h plus 100 USD fix, Webinar 60, E-Learning 25).",
            "Die Arbeitszeit der Lernenden wird mit 85 USD/h als Opportunitätskosten mitgerechnet.",
            "Teure Präsenzformate verlieren gegen günstige Webinare, sobald sie dieselbe Lücke schliessen.",
        ],
        watch: "Programmkosten",
        patch: {
            ranking: { denominator: "usd" },
            cost: { opportunityUsdPerHour: 85 },
            evidence: { floor: "wissenstest", gateObligationDischarge: true },
        },
        profile: { nachweis: 3, kosten: 4, transfer: 3, modalitaet: 1, wiederholung: 1 },
        source: "https://roiinstitute.net/roi-methodology/",
    },
    {
        id: "ltem",
        name: "LTEM",
        author: "Will Thalheimer",
        year: 2018,
        family: "ladder",
        claim: "Acht Stufen statt vier. Anwesenheit und Wissensabfrage belegen kein Lernen — erst Entscheidungs- und Handlungskompetenz zählen.",
        changes: [
            "Kompetenz wird nur noch anerkannt, wenn ein Entscheidungstest oder ein Transfernachweis vorliegt.",
            "Ein bestandener Wissenstest zählt nicht mehr als Kompetenznachweis (LTEM-Stufe 4 statt 5).",
            "Bereits geschlossene Lücken gehen wieder auf — der Plan wächst, ohne dass sich eine einzige Person verändert hat.",
            "Die Pflicht-Abdeckung bleibt unangetastet: LTEM bewertet Kompetenz, nicht Compliance.",
        ],
        watch: "Geplante Stunden",
        patch: {
            evidence: { floor: "entscheidungstest", gateSkillCredit: true },
        },
        profile: { nachweis: 4, kosten: 1, transfer: 4, modalitaet: 2, wiederholung: 2 },
        source: "https://www.worklearning.com/ltem/",
    },
    {
        id: "brinkerhoff",
        name: "Success Case Method",
        author: "Robert O. Brinkerhoff",
        year: 2003,
        family: "overlay",
        claim: "Nicht der Durchschnitt zählt, sondern die Ausreisser: wer hat es angewendet, wer nicht — und woran lag es.",
        changes: [
            "Kompetenz wird nur anerkannt, wenn die Anwendung im Alltag bestätigt wurde — unabhängig von jedem Testergebnis.",
            "Selbstauskunft allein genügt; entscheidend ist, dass überhaupt jemand hingeschaut hat.",
            "Macht sichtbar, wie viel des Katalogs nie bestätigt wurde: die Nicht-Erfolgsfälle sind die eigentliche Erkenntnis.",
        ],
        watch: "Bestätigter Transfer",
        patch: {
            evidence: { gateSkillCredit: true, requireBehaviour: true },
        },
        profile: { nachweis: 2, kosten: 1, transfer: 4, modalitaet: 1, wiederholung: 1 },
        source: "https://www.watershedlrs.com/blog/learning-evaluation/brinkerhoff-method/",
    },
    {
        id: "seventy-twenty-ten",
        name: "70-20-10",
        author: "McCall, Lombardo & Eichinger",
        year: 1996,
        family: "design",
        claim: "70 % der Entwicklung passiert bei der Arbeit, 20 % im Austausch, 10 % im Kurs — der Plan sollte das abbilden.",
        changes: [
            "Der Composer steuert auf einen Modalitätsmix von 70 / 20 / 10 zu.",
            "Hospitationen, Mentoring, Fachzirkel und Praxisaufträge rücken vor formale Kurse, solange ihre Modalität untervertreten ist.",
            "Keine Quote: ein nötiger Kurs wird nie zugunsten einer Prozentzahl gestrichen — die Rangfolge entscheidet weiterhin.",
        ],
        watch: "Modalitätsmix",
        patch: { mix: { enabled: true } },
        profile: { nachweis: 2, kosten: 2, transfer: 4, modalitaet: 4, wiederholung: 2 },
        source: "https://trainingindustry.com/wiki/content-development/the-702010-model-for-learning-and-development/",
    },
    {
        id: "spacing",
        name: "Spacing-Effekt",
        author: "Hermann Ebbinghaus",
        year: 1885,
        family: "design",
        claim: "Die Vergessenskurve ist der älteste belastbare Befund der Lernforschung — und der, den Kurskataloge am zuverlässigsten ignorieren.",
        changes: [
            "Nach jeder Pflichtschulung wird 30 Tage später eine kurze Auffrischung eingeplant.",
            "Die Auffrischung ist kein Kurs, sondern ein terminierter Abruf — eine Stunde, im Selbststudium.",
            "Sie wird nur eingeplant, wo das Quartalsbudget sie trägt: eine Auffrischung, die eine Pflicht verdrängt, verkehrt ihren Zweck.",
        ],
        watch: "Quartalsauslastung",
        patch: { reinforcement: { enabled: true } },
        profile: { nachweis: 1, kosten: 2, transfer: 3, modalitaet: 1, wiederholung: 4 },
        source: "https://www.growthengineering.co.uk/learning-models/",
    },
]

export const FRAMEWORK_BY_ID: ReadonlyMap<FrameworkId, LearningFramework> = new Map(
    FRAMEWORKS.map((framework) => [framework.id, framework]),
)

/**
 * Merge the selected frameworks into one policy patch.
 *
 * Order matters and is deliberate: `FRAMEWORKS` order is applied, so a design
 * framework never silently overwrites a ladder's evidence settings. Selecting
 * two ladders is rejected rather than merged — see `conflictsIn`.
 */
export function frameworkPatch(selected: FrameworkId[]): ComposerPolicy {
    const chosen = FRAMEWORKS.filter((framework) => selected.includes(framework.id))
    return chosen.reduce<ComposerPolicy>((merged, framework) => deepMerge(merged, framework.patch), {})
}

/** Which selections cannot be held at once. Empty means the combination is sound. */
export function conflictsIn(selected: FrameworkId[]): FrameworkId[] {
    const ladders = selected.filter((id) => FRAMEWORK_BY_ID.get(id)?.family === "ladder")
    return ladders.length > 1 ? ladders : []
}

/** The combined emphasis profile, for the radar. Max per axis, not a sum. */
export function frameworkProfile(selected: FrameworkId[]): Record<FrameworkAxis, number> {
    const profile: Record<FrameworkAxis, number> = {
        nachweis: 0,
        kosten: 0,
        transfer: 0,
        modalitaet: 0,
        wiederholung: 0,
    }
    for (const id of selected) {
        const framework = FRAMEWORK_BY_ID.get(id)
        if (!framework) continue
        for (const axis of FRAMEWORK_AXES) {
            profile[axis] = Math.max(profile[axis], framework.profile[axis])
        }
    }
    return profile
}

function deepMerge<T extends object>(base: T, patch: object): T {
    const result: Record<string, unknown> = { ...(base as Record<string, unknown>) }
    for (const [key, value] of Object.entries(patch as Record<string, unknown>)) {
        if (value === undefined) continue
        const existing = result[key]
        result[key] =
            value !== null &&
            typeof value === "object" &&
            existing !== null &&
            typeof existing === "object"
                ? deepMerge(existing as object, value as object)
                : value
    }
    return result as T
}
